<script setup>
import { computed } from 'vue';
import { frontendURL } from 'dashboard/helper/URLHelper.js';
import { dynamicTime } from 'shared/helpers/timeHelper';
import { useMapGetter } from 'dashboard/composables/store';
import { ATTACHMENT_TYPES } from 'dashboard/components-next/message/constants.js';

import { Item, ItemContent, ItemMedia, ItemTitle } from 'next/polaris/ui/item';
import Avatar from 'dashboard/components-next/avatar/Avatar.vue';
import ChannelIcon from 'dashboard/components-next/icon/ChannelIcon.vue';
import Icon from 'dashboard/components-next/icon/Icon.vue';
import FileChip from 'next/message/chips/File.vue';
import AudioChip from 'next/message/chips/Audio.vue';
import TranscribedText from './TranscribedText.vue';

const props = defineProps({
  id: {
    type: Number,
    default: 0,
  },
  inboxId: {
    type: Number,
    default: 0,
  },
  isPrivate: {
    type: Boolean,
    default: false,
  },
  accountId: {
    type: [String, Number],
    default: '',
  },
  createdAt: {
    type: [String, Date, Number],
    default: '',
  },
  messageId: {
    type: Number,
    default: 0,
  },
  authorName: {
    type: String,
    default: '',
  },
  authorThumbnail: {
    type: String,
    default: '',
  },
  attachments: {
    type: Array,
    default: () => [],
  },
});

const inboxGetter = useMapGetter('inboxes/getInbox');
const inbox = computed(() =>
  props.inboxId ? inboxGetter.value(props.inboxId) : null
);

const navigateTo = computed(() => {
  const params = {};
  if (props.messageId) {
    params.messageId = props.messageId;
  }
  return frontendURL(
    `accounts/${props.accountId}/conversations/${props.id}`,
    params
  );
});

const createdAtTime = computed(() => {
  if (!props.createdAt) return '';
  return dynamicTime(props.createdAt);
});

const inboxName = computed(() => inbox.value?.name);

const fileAttachments = computed(() => {
  return props.attachments.filter(
    attachment => attachment.fileType !== ATTACHMENT_TYPES.AUDIO
  );
});

const audioAttachments = computed(() => {
  return props.attachments.filter(
    attachment => attachment.fileType === ATTACHMENT_TYPES.AUDIO
  );
});
</script>

<template>
  <Item
    as-child
    size="sm"
    class="w-full cursor-pointer flex-nowrap items-start px-3 text-start text-polaris-foreground hover:bg-polaris-accent"
  >
    <!-- A message hit in the shape of the 3.1 conversation row: the snippet (slot) is the description; the whole row is the link. -->
    <router-link :to="navigateTo">
      <ItemMedia>
        <Avatar
          :name="authorName"
          :src="authorThumbnail"
          :size="32"
          rounded-full
        />
      </ItemMedia>
      <ItemContent class="min-w-0 gap-0.5">
        <ItemTitle class="w-full justify-between font-normal">
          <span class="truncate">{{ authorName }}</span>
          <span
            v-if="createdAtTime"
            class="shrink-0 text-xs font-normal text-polaris-muted-foreground"
          >
            {{ createdAtTime }}
          </span>
        </ItemTitle>
        <p
          class="mb-0 flex min-w-0 items-center gap-1.5 type-polaris-small-label text-polaris-muted-foreground"
        >
          <span class="shrink-0 font-medium">{{ `#${id}` }}</span>
          <template v-if="inboxName">
            <span
              class="size-0.5 shrink-0 rounded-full bg-current"
              aria-hidden="true"
            />
            <ChannelIcon
              v-if="inbox?.id"
              :inbox="inbox"
              class="size-3.5 shrink-0"
            />
            <span class="truncate" :title="inboxName">{{ inboxName }}</span>
          </template>
          <template v-if="isPrivate">
            <span
              class="size-0.5 shrink-0 rounded-full bg-current"
              aria-hidden="true"
            />
            <span class="flex shrink-0 items-center gap-1">
              <Icon icon="i-lucide-lock-keyhole" class="size-3 shrink-0" />
              {{ $t('SEARCH.PRIVATE') }}
            </span>
          </template>
        </p>
        <slot />
        <div v-if="audioAttachments.length" class="mt-1.5 w-full space-y-3">
          <div
            v-for="attachment in audioAttachments"
            :key="attachment.id"
            class="w-full"
          >
            <AudioChip
              class="bg-polaris-muted text-polaris-foreground dark:bg-polaris-muted"
              :attachment="attachment"
              :show-transcribed-text="false"
              @click.prevent
            />
            <div v-if="attachment.transcribedText" class="pt-2">
              <TranscribedText :text="attachment.transcribedText" />
            </div>
          </div>
        </div>
        <div
          v-if="fileAttachments.length"
          class="mt-1.5 flex flex-wrap items-center gap-2"
        >
          <FileChip
            v-for="attachment in fileAttachments"
            :key="attachment.id"
            :attachment="attachment"
            class="!h-8"
            @click.stop
          />
        </div>
      </ItemContent>
    </router-link>
  </Item>
</template>
